import Link from 'next/link';
import styled, { css } from 'styled-components';
import { useRouter } from 'next/router';

import { ACCOUNT_LIST } from 'profile/enums/constants';
import {
  AccountDropdownMenuButton,
  AccountDropdownMenuList,
  AccountDropdownMenuWrapper,
  AccountDropdownMenuListItem,
} from 'layout/header/styles/AccountDropdownMenu';
import { bugsnag } from 'shared/utils/services/bugsnag';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { showOnMobileAndTabletOnly } from 'styles/utils/responsiveness';
import { UnorderedList } from 'shared/components/UnorderedList';
import { useAccount } from 'account/hooks/useAccount';
import { t } from 'localization/utils/t';
import { variableFontRegular } from 'styles/theme';

const TP = 'layout.header.components.LoggedInDropdownMenu';

export const LoggedInDropdownMenu = () => {
  const { logOutAccount } = useAccount();
  const { pathname } = useRouter();
  const selectedTab = pathname?.split('/')?.[2];

  const handleLogOutClick = async () => {
    sendTrackingEvent('LOG_OUT_TAP');
    try {
      await logOutAccount();
    } catch (error) {
      bugsnag(error, { context: 'LoggedInDropdownMenu' });
    }
  };

  const handleAnalyticsClick = (section) => {
    sendTrackingEvent('ACCOUNT_HEADER_NAVIGATION_TAP', { section });
  };

  return (
    <LoggedInDropdownMenu.Wrapper>
      <LoggedInDropdownMenu.List>
        <LoggedInDropdownMenu.Block>
          <LoggedInDropdownMenu.ListItem>
            <Link href="/account/wants" passHref legacyBehavior>
              <LoggedInDropdownMenu.Link
                data-qa="open_wants_page"
                $isSelected={pathname === '/wants'}
                onClick={() => handleAnalyticsClick('wants')}
              >
                {t(`${TP}.wants`, 'Wants')}
              </LoggedInDropdownMenu.Link>
            </Link>
          </LoggedInDropdownMenu.ListItem>
          <LoggedInDropdownMenu.ListItem>
            <Link href="/account/orders" passHref legacyBehavior>
              <LoggedInDropdownMenu.Link
                data-qa="account_nav_orders"
                $isSelected={selectedTab === ACCOUNT_LIST.ORDERS}
                onClick={() => handleAnalyticsClick('orders')}
              >
                {t(`${TP}.orders`, 'Orders')}
              </LoggedInDropdownMenu.Link>
            </Link>
          </LoggedInDropdownMenu.ListItem>
          <LoggedInDropdownMenu.ListItem>
            <Link href="/account/offers" passHref legacyBehavior>
              <LoggedInDropdownMenu.Link
                data-qa="account_nav_offers"
                $isSelected={selectedTab === ACCOUNT_LIST.OFFERS}
                onClick={() => handleAnalyticsClick('offers')}
              >
                {t(`${TP}.offers`, 'Offers')}
              </LoggedInDropdownMenu.Link>
            </Link>
          </LoggedInDropdownMenu.ListItem>
          <LoggedInDropdownMenu.ListItem>
            <Link href="/account/profile" passHref legacyBehavior>
              <LoggedInDropdownMenu.Link
                data-qa="account_nav_profile"
                $isSelected={selectedTab === ACCOUNT_LIST.PROFILE}
                onClick={() => handleAnalyticsClick('profile')}
              >
                {t(`${TP}.profile`, 'Profile')}
              </LoggedInDropdownMenu.Link>
            </Link>
          </LoggedInDropdownMenu.ListItem>
          <LoggedInDropdownMenu.ListItem>
            <Link href="/account/preferences" passHref legacyBehavior>
              <LoggedInDropdownMenu.Link
                data-qa="account_nav_preferences"
                $isSelected={selectedTab === ACCOUNT_LIST.PREFERENCES}
                onClick={() => handleAnalyticsClick('preferences')}
              >
                {t(`${TP}.preferences`, 'Preferences')}
              </LoggedInDropdownMenu.Link>
            </Link>
          </LoggedInDropdownMenu.ListItem>
          <LoggedInDropdownMenu.ListItem $hideOnDesktop>
            <Link href="/account/addresses" passHref legacyBehavior>
              <LoggedInDropdownMenu.Link
                data-qa="account_nav_addresses"
                $isSelected={selectedTab === ACCOUNT_LIST.ADDRESSES}
                onClick={() => handleAnalyticsClick('shipping_addresses')}
              >
                {t(`${TP}.shippingAddresses`, 'Shipping Addresses')}
              </LoggedInDropdownMenu.Link>
            </Link>
          </LoggedInDropdownMenu.ListItem>
          <LoggedInDropdownMenu.ListItem $hideOnDesktop>
            <Link href="/account/payments" passHref legacyBehavior>
              <LoggedInDropdownMenu.Link
                data-qa="account_nav_payments"
                $isSelected={selectedTab === ACCOUNT_LIST.PAYMENTS}
                onClick={() => handleAnalyticsClick('payment_methods')}
              >
                {t(`${TP}.paymentMethods`, 'Payment Methods')}
              </LoggedInDropdownMenu.Link>
            </Link>
          </LoggedInDropdownMenu.ListItem>
          <LoggedInDropdownMenu.ListItem>
            <Link href="https://support.goat.com/" passHref legacyBehavior>
              <LoggedInDropdownMenu.Link
                data-qa="account_nav_support"
                $isSelected={selectedTab === ACCOUNT_LIST.SUPPORT}
                onClick={() => handleAnalyticsClick('support')}
              >
                {t(`${TP}.support`, 'Support')}
              </LoggedInDropdownMenu.Link>
            </Link>
          </LoggedInDropdownMenu.ListItem>
          <LoggedInDropdownMenu.ListItem>
            <button
              data-qa="log_out_menu_button"
              className="logOutButton"
              onClick={handleLogOutClick}
              type="button"
            >
              {t(`${TP}.logout`, 'Log Out')}
            </button>
          </LoggedInDropdownMenu.ListItem>
        </LoggedInDropdownMenu.Block>
      </LoggedInDropdownMenu.List>
    </LoggedInDropdownMenu.Wrapper>
  );
};

LoggedInDropdownMenu.Wrapper = styled.div`
  ${AccountDropdownMenuWrapper};
  ${variableFontRegular({ height: '12px' })};
`;

LoggedInDropdownMenu.Link = styled.a<{ $isSelected?: boolean }>`
  text-decoration: ${({ $isSelected }) => $isSelected && 'underline'};
`;

LoggedInDropdownMenu.Block = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 0;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumLarge}px) {
    align-items: flex-end;
  }
`;

LoggedInDropdownMenu.List = styled(UnorderedList)`
  ${AccountDropdownMenuList};
`;

LoggedInDropdownMenu.ListItem = styled.li<{ $hideOnDesktop?: boolean }>`
  padding-bottom: 16px;
  ${AccountDropdownMenuListItem};

  button {
    ${AccountDropdownMenuButton};
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumLarge}px) {
    padding-bottom: 8px;

    ${({ $hideOnDesktop }) =>
      $hideOnDesktop &&
      css`
        ${showOnMobileAndTabletOnly};
      `};
  }
`;
